/* eslint-disable camelcase */
import { useEffect, useState } from 'react'
import { ContentfulLivePreview, LivePreviewProps } from '@contentful/live-preview'
import { useContentfulInspectorMode, useContentfulLiveUpdates } from '@contentful/live-preview/react'
import { Entry, EntrySkeletonType, ContentTypeCollection, ContentTypeField, ContentTypeFieldType } from 'contentful'
import { sanitize } from 'dompurify'
import { capitalize } from './strings'

export type ContentfulEntry = Entry<EntrySkeletonType, 'WITHOUT_UNRESOLVABLE_LINKS'>

/**
 * Convert SDK type name to GQL type name
 */
function typeNameToContentTypeId(typeName: string) {
  if (!typeName) return undefined
  return `Contentful${capitalize(typeName)}`
}

/**
 * Transform data by Contentful type
 * - Mainly used to match variable names that have been renamed in GQL queries
 */
const GQL_TRANSFORM_CONFIG = {
  ContentfulTableRow: ({ title, ...data }: any) => ({
    ...data,
    rowTitle: title,
  }),
  ContentfulTableRowSectionHeading: ({ title, ...data }: any) => ({
    ...data,
    sectionTitle: title,
  }),
  ContentfulComponentCtaLink: ({ style, ...data }: any) => ({
    ...data,
    variant: style,
  }),
  ContentfulComponentHubspotCta: ({ variant, ...data }: any) => ({
    ...data,
    style: variant,
  }),
  ContentfulSectionBarGraph: ({ stats, ...data }: any) => ({
    ...data,
    items: stats,
  }),
  ContentfulNinetailedExperience: ({ nt_audience, nt_name, nt_type, nt_config, ...data }: any) => ({
    ...data,
    audience: nt_audience,
    name: nt_name,
    type: nt_type,
    config: nt_config,
  }),
  ContentfulSectionSetOfPricing: ({ highlightTerms, ...data }) => ({
    ...data,
    highlightTerms: highlightTerms?.map(({ key, value }) => ({
      term: key,
      definition: value,
    })),
  }),
}

/**
 * Transform data by field type
 */
type FieldTransfromArgs = {
  field: ContentTypeField
  value: any
}
const FIELD_TRANSFORM_CONFIG: Record<ContentTypeFieldType, (args: FieldTransfromArgs) => any | null> = {
  Text: ({ field, value }) => ({
    [field.id]: value,
  }),
  RichText: ({ value }) =>
    value
      ? {
          ...value,
          raw: JSON.stringify(value),
        }
      : null,
  Symbol: null,
  Integer: null,
  Number: null,
  Date: null,
  Boolean: null,
  Location: null,
  Link: null,
  Array: null,
  Object: null,
  ResourceLink: null,
}

const transformData = (data: any, contentTypes: ContentTypeCollection) => {
  const typeName = data?.__typename
  if (!typeName) return data
  let transformedData = { ...data }

  const contentType = contentTypes?.items.find((item) => typeName === typeNameToContentTypeId(item.sys.id))
  if (contentType) {
    transformedData = contentType.fields.reduce((acc, field) => {
      const { id, type } = field
      const fieldTransform = FIELD_TRANSFORM_CONFIG[type]
      if (fieldTransform) {
        return { ...acc, [id]: fieldTransform({ field, value: data[id] }) }
      }
      return acc
    }, transformedData)
  }

  const gqlTransform = GQL_TRANSFORM_CONFIG[typeName]
  if (gqlTransform) {
    transformedData = gqlTransform(transformedData)
  }

  return transformedData
}

const isContentfulDocument = (doc: any) => !!doc && typeof doc === 'object' && 'fields' in doc && 'sys' in doc
const isSvgWithoutContent = (image: any) => image?.file?.contentType === 'image/svg+xml' && !image?.svg?.content

export async function formatContentfulData(data, contentTypes: ContentTypeCollection) {
  if (typeof data === 'object' && data !== null) {
    if (isSvgWithoutContent(data)) {
      // Download SVG content
      const content = await fetch(data.file.url)
        .then((res) => res.text())
        .then((svg) => sanitize(svg))
        .catch(() => '')
      data.svg = { content }
    }

    if (isContentfulDocument(data)) {
      const typeName = data.sys.contentType?.sys?.id
      return transformData(
        {
          ...(await formatContentfulData(data['fields'], contentTypes)),
          id: data.sys.id,
          contentful_id: data.sys.id,
          __typename: typeNameToContentTypeId(typeName),
        },
        contentTypes
      )
    }

    const newObj: Record<string, any> = Array.isArray(data) ? [] : {}
    await Promise.all(
      Object.entries(transformData(data, contentTypes)).map(async ([key, value]) => {
        newObj[key] = await formatContentfulData(value, contentTypes)
      })
    )
    return newObj
  }
  if (Array.isArray(data)) {
    return Promise.all(data.map((item) => formatContentfulData(item, contentTypes)))
  }
  return data
}

export function useLiveUpdates(entry: ContentfulEntry, contentTypes: ContentTypeCollection): Record<string, any> {
  const { locale } = ContentfulLivePreview
  const liveData = useContentfulLiveUpdates(entry, { locale })
  const [data, setData] = useState(null)
  useEffect(() => {
    const updateData = async () => {
      setData(await formatContentfulData(liveData, contentTypes))
    }
    updateData()
  }, [liveData])
  return data
}

export function getInspectorProps(props: LivePreviewProps): ReturnType<typeof inspectorProps> {
  const inspectorProps = useContentfulInspectorMode()
  return ContentfulLivePreview.liveUpdatesEnabled ? inspectorProps(props) : ({} as ReturnType<typeof inspectorProps>)
}
export type InspectorModeTags = ReturnType<typeof getInspectorProps>
